<template>
  <div
    ref="referenceRef"
    class="relative"
  >
    <div
      :id="id"
      ref="selectTriggerRef"
      role="combobox"
      :aria-controls="listboxId"
      :aria-expanded="isOpen"
      aria-label="Select one option"
      :aria-activedescendant="selectedOption ? `${listboxId}-${selectedOption.value}` : undefined"
      class="typography-text-base relative flex cursor-pointer items-center justify-center gap-2 rounded-md px-4 py-2 font-normal
      shadow-sm ring-1 ring-inset ring-neutral-700 hover:!bg-neutral-100 hover:ring-primary-700 focus:ring-2
      focus:ring-primary-700 focus-visible:outline focus-visible:outline-offset active:ring-2 active:ring-primary-700 mobile:!leading-8"
      tabindex="0"
      @keydown="e => keyHandler(e, [], () => toggle())"
      @click="toggle()"
    >
      <SfIconUnfoldMore
        class="text-neutral-900"
        size="sm"
      />
      <span class="text-neutral-900">
        {{ selectedOption ? $t(selectedOption.label) : $t(title) }}
      </span>
    </div>
    <ul
      v-show="isOpen"
      :id="listboxId"
      ref="floatingRef"
      role="listbox"
      aria-label="Select one option"
      class="z-10 w-full min-w-44 rounded-md border border-neutral-200 bg-white py-2 shadow-sm"
      :style="dropdownStyle"
    >
      <SfListItem
        v-for="option in options"
        :id="`${listboxId}-${option.value}`"
        :key="option.value"
        role="option"
        tabindex="0"
        :aria-selected="option.value === selectedOption?.value"
        class="block text-base"
        :class="{ 'bg-neutral-50': option.value === selectedOption?.value }"
        @click="selectOption(option)"
        @keydown="e => keyHandler(e, [], () => selectOption(option))"
      >
        {{ $t(option.label) }}
      </SfListItem>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { ref, type Ref } from 'vue';
import { unrefElement } from '@vueuse/core';
import {
  useDropdown,
  useDisclosure,
  SfListItem,
  useId,
  useTrapFocus,
  SfIconUnfoldMore,
} from '@storefront-ui/vue';
import keyHandler from '~/helpers/a11y/keyHandler';

interface Props {
  options: SelectOption[];
  title: string;
}

withDefaults(defineProps<Props>(), {
  title: 'Choose from the list',
});

type SelectOption = {
  label: string;
  value: string;
};

const emit = defineEmits(['on-apply']);

const { close, toggle, isOpen } = useDisclosure({ initialValue: false });
const selectedOption = ref<SelectOption>();
const id = useId();
const listboxId = `select-dropdown-${id}`;
const selectTriggerRef = ref<HTMLDivElement>();

const {
  referenceRef,
  floatingRef,
  style: dropdownStyle,
} = useDropdown({
  isOpen,
  onClose: close,
});

useTrapFocus(floatingRef as Ref<HTMLUListElement>, {
  arrowKeysUpDown: true,
  activeState: isOpen,
  initialFocusContainerFallback: true,
});

const selectOption = (option: SelectOption) => {
  selectedOption.value = option;
  close();
  unrefElement(selectTriggerRef as Ref<HTMLDivElement>)?.focus();
  emit('on-apply', option.value);
};
</script>
